import React from 'react';
import { Button, CircularProgress, styled } from '@mui/material';

/**
 * Consistent button styles for the form application.
 * Use these components instead of raw MUI Button to maintain consistency.
 *
 * Usage:
 *   import { PrimaryButton, SecondaryButton, TextButton } from './common/FormButtons';
 *
 *   <PrimaryButton onClick={handleSubmit}>Submit</PrimaryButton>
 *   <SecondaryButton startIcon={<ArrowBack />}>Back</SecondaryButton>
 *   <TextButton>Cancel</TextButton>
 */

// Base button styles shared across all variants
const baseButtonStyles = {
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: 1.5,
  transition: 'all 0.2s ease',
};

// Size variants
const sizeStyles = {
  small: {
    padding: '6px 12px',
    fontSize: '13px',
  },
  medium: {
    padding: '8px 16px',
    fontSize: '14px',
  },
  large: {
    padding: '10px 24px',
    fontSize: '15px',
  },
};

/**
 * Primary Button - Main action button (submit, continue, confirm)
 * Use for the primary action in a dialog or form
 */
export const PrimaryButton = styled(Button)(({ size = 'medium' }) => ({
  ...baseButtonStyles,
  ...sizeStyles[size],
  backgroundColor: '#333333',
  color: '#ffffff',
  border: 'none',
  '&:hover': {
    backgroundColor: '#444444',
  },
  '&:active': {
    backgroundColor: '#222222',
  },
  '&:disabled': {
    backgroundColor: '#e0e0e0',
    color: '#9e9e9e',
  },
}));

/**
 * Secondary Button - Outlined style for secondary actions
 * Use for actions like "Back", "Cancel" with action, or alternative options
 */
export const SecondaryButton = styled(Button)(({ size = 'medium' }) => ({
  ...baseButtonStyles,
  ...sizeStyles[size],
  backgroundColor: 'transparent',
  color: '#333333',
  border: '1px solid #333333',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderColor: '#222222',
  },
  '&:active': {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
  },
  '&:disabled': {
    borderColor: '#e0e0e0',
    color: '#9e9e9e',
  },
}));

/**
 * Neutral Button - Outlined style with neutral/gray colors
 * Use for "Cancel", "Close", or dismissive actions
 */
export const NeutralButton = styled(Button)(({ size = 'medium' }) => ({
  ...baseButtonStyles,
  ...sizeStyles[size],
  backgroundColor: 'transparent',
  color: '#555555',
  border: '1px solid #dddddd',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#bbbbbb',
  },
  '&:active': {
    backgroundColor: '#eeeeee',
  },
  '&:disabled': {
    borderColor: '#e0e0e0',
    color: '#9e9e9e',
  },
}));

/**
 * Text Button - No border, minimal style
 * Use for tertiary actions or links within content
 */
export const TextButton = styled(Button)(({ size = 'medium' }) => ({
  ...baseButtonStyles,
  ...sizeStyles[size],
  backgroundColor: 'transparent',
  color: '#555555',
  border: 'none',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  '&:active': {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
  },
  '&:disabled': {
    color: '#9e9e9e',
  },
}));

/**
 * Danger Button - For destructive actions
 * Use for delete, remove, or other destructive operations
 */
export const DangerButton = styled(Button)(({ size = 'medium' }) => ({
  ...baseButtonStyles,
  ...sizeStyles[size],
  backgroundColor: '#d32f2f',
  color: '#ffffff',
  border: 'none',
  '&:hover': {
    backgroundColor: '#c62828',
  },
  '&:active': {
    backgroundColor: '#b71c1c',
  },
  '&:disabled': {
    backgroundColor: '#e0e0e0',
    color: '#9e9e9e',
  },
}));

/**
 * Danger Outlined Button - For destructive actions with less emphasis
 */
export const DangerOutlinedButton = styled(Button)(({ size = 'medium' }) => ({
  ...baseButtonStyles,
  ...sizeStyles[size],
  backgroundColor: 'transparent',
  color: '#d32f2f',
  border: '1px solid #d32f2f',
  '&:hover': {
    backgroundColor: 'rgba(211, 47, 47, 0.04)',
    borderColor: '#c62828',
  },
  '&:active': {
    backgroundColor: 'rgba(211, 47, 47, 0.08)',
  },
  '&:disabled': {
    borderColor: '#e0e0e0',
    color: '#9e9e9e',
  },
}));

/**
 * Success Button - For positive confirmations
 * Use for save, complete, or success actions
 */
export const SuccessButton = styled(Button)(({ size = 'medium' }) => ({
  ...baseButtonStyles,
  ...sizeStyles[size],
  backgroundColor: '#4caf50',
  color: '#ffffff',
  border: 'none',
  '&:hover': {
    backgroundColor: '#43a047',
  },
  '&:active': {
    backgroundColor: '#388e3c',
  },
  '&:disabled': {
    backgroundColor: '#e0e0e0',
    color: '#9e9e9e',
  },
}));

/**
 * Loading Button wrapper - Add loading state to any button
 *
 * Usage:
 *   <LoadingButton loading={isLoading} onClick={handleClick}>
 *     Submit
 *   </LoadingButton>
 */
export const LoadingButton = ({
  loading,
  children,
  disabled,
  variant = 'primary',
  size = 'medium',
  ...props
}) => {
  const ButtonComponent =
    {
      primary: PrimaryButton,
      secondary: SecondaryButton,
      neutral: NeutralButton,
      text: TextButton,
      danger: DangerButton,
      success: SuccessButton,
    }[variant] || PrimaryButton;

  return (
    <ButtonComponent disabled={disabled || loading} size={size} {...props}>
      {loading ? (
        <CircularProgress
          size={size === 'small' ? 16 : size === 'large' ? 22 : 18}
          sx={{
            color:
              variant === 'primary' ||
              variant === 'danger' ||
              variant === 'success'
                ? '#fff'
                : '#333',
          }}
        />
      ) : (
        children
      )}
    </ButtonComponent>
  );
};

/**
 * Button Group Container - For consistent spacing between buttons
 */
export const ButtonGroup = styled('div')({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  flexWrap: 'wrap',
});

/**
 * Button Group with right alignment - Common for dialog actions
 */
export const ButtonGroupRight = styled('div')({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
});

/**
 * Button Group with space between - Cancel on left, actions on right
 */
export const ButtonGroupSpaced = styled('div')({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  width: '100%',
});

export default {
  PrimaryButton,
  SecondaryButton,
  NeutralButton,
  TextButton,
  DangerButton,
  DangerOutlinedButton,
  SuccessButton,
  LoadingButton,
  ButtonGroup,
  ButtonGroupRight,
  ButtonGroupSpaced,
};
